/**
 * One toast at a time, with an Undo.
 *
 * The toast exists because **archive is the safe default** (PRD 04 §9): it has to be
 * instantly reversible, and an action a parent cannot take back is not the everyday one.
 * Archive/restore are plain owner updates with no server round-trip, which is exactly why
 * Undo can be immediate rather than optimistic-then-sorry.
 *
 * The queue is React Aria's now (`useStudioToastState`), so the message is announced without
 * stealing focus, hovering or focusing it pauses the timer — which matters when the message
 * carries the only Undo — and the region is a landmark a keyboard reaches with F6. The
 * provider's own API is unchanged: `show({ message, undoLabel, onUndo })`.
 */

import { ToastRegion, useStudioToastState } from '@atlas-nook/design-system';
import type { ToastContent } from '@atlas-nook/design-system';
import { createContext, useCallback, useContext, useMemo } from 'react';
import type { JSX, ReactNode } from 'react';

import { useT } from '../i18n/I18nProvider.js';

const DISMISS_MS = 8000;

interface ToastValue {
  show: (toast: ToastContent) => void;
  dismiss: () => void;
}

const ToastContext = createContext<ToastValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const t = useT();
  const state = useStudioToastState();

  const dismiss = useCallback(() => {
    for (const toast of [...state.visibleToasts]) state.close(toast.key);
  }, [state]);

  const show = useCallback(
    (next: ToastContent) => {
      // One at a time: the queue's `maxVisibleToasts` is 1, so a second message replaces
      // the first rather than stacking a column of them over the surface.
      for (const toast of [...state.visibleToasts]) state.close(toast.key);
      state.add(
        {
          message: next.message,
          ...(next.undoLabel !== undefined ? { undoLabel: next.undoLabel } : {}),
          ...(next.onUndo !== undefined ? { onUndo: next.onUndo } : {}),
        },
        { timeout: DISMISS_MS },
      );
    },
    [state],
  );

  const value = useMemo<ToastValue>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastRegion state={state} label={t('toast.region')} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  const value = useContext(ToastContext);
  // Outside a provider a toast is a no-op rather than a crash: a message is a courtesy,
  // and losing one must never take the surface down with it.
  return value ?? { show: () => undefined, dismiss: () => undefined };
}
